/**
 * Angebot schreiben.
 *
 * Fast dieselbe Maske wie bei einer Rechnung — mit zwei Unterschieden, die
 * fachlich sind und nicht kosmetisch: Ein Angebot hat eine Gültigkeit statt
 * eines Zahlungsziels, und es trägt keinen Zahlungscode. Es ist nichts zu
 * zahlen.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Textbereich, Textfeld } from '../bausteine/Feld';
import { KontaktWahl } from '../bausteine/KontaktWahl';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import {
  alsEingabe,
  alsZeilen,
  leerePosition,
  Positionen,
  type PositionZeile,
} from '../bausteine/Positionen';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { heute } from '../lib/format';
import type { Angebot, KontaktListe, Unternehmen } from '../lib/typen';

/** Dieselbe Maske legt an und ändert — geändert wird nur der Entwurf. */
export function AngebotNeu(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const bestand = useAbruf<Angebot>(id ? `/api/angebote/${id}` : null);

  if (id && !bestand.daten) {
    return (
      <Seite titel="Angebot ändern">
        {bestand.fehler ? <Fehlermeldung text={bestand.fehler} /> : <Laedt />}
      </Seite>
    );
  }

  return <Formular key={bestand.daten?.id ?? 'neu'} bestand={bestand.daten} />;
}

function Formular({ bestand }: { bestand: Angebot | null }): ReactNode {
  const navigiere = useNavigate();
  const vorgang = useVorgang();

  const kontakte = useAbruf<KontaktListe>('/api/kontakte?typ=kunde&proSeite=200');
  const unternehmen = useAbruf<Unternehmen>('/api/unternehmen');

  const [kontaktId, setzeKontaktId] = useState(bestand?.kontaktId ?? '');
  const [angebotsdatum, setzeAngebotsdatum] = useState(bestand?.angebotsdatum ?? heute());
  const [gueltigkeit, setzeGueltigkeit] = useState(bestand ? '' : '30');
  const [betreff, setzeBetreff] = useState(bestand?.betreff ?? '');
  const [leistungVon, setzeLeistungVon] = useState(bestand?.leistungVon ?? '');
  const [leistungBis, setzeLeistungBis] = useState(bestand?.leistungBis ?? '');
  const [einleitung, setzeEinleitung] = useState(bestand?.einleitungstext ?? '');
  const [schluss, setzeSchluss] = useState(bestand?.schlusstext ?? '');
  const [zeilen, setzeZeilen] = useState<PositionZeile[]>(
    bestand && bestand.positionen.length > 0 ? alsZeilen(bestand.positionen) : [leerePosition()],
  );

  const kleinunternehmer = unternehmen.daten?.kleinunternehmer ?? true;
  const kunden = kontakte.daten?.kontakte ?? [];
  const fertig =
    kontaktId !== '' && zeilen.some((z) => z.bezeichnung.trim() !== '' && z.einzelpreis !== '');

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!fertig) return;

    await vorgang.fuehreAus(async () => {
      const daten = {
        kontaktId,
        angebotsdatum,
        ...(gueltigkeit ? { gueltigkeitTage: Number.parseInt(gueltigkeit, 10) } : {}),
        betreff: betreff.trim() || null,
        leistungVon: leistungVon || null,
        leistungBis: leistungBis || null,
        ...(einleitung.trim() ? { einleitungstext: einleitung.trim() } : {}),
        ...(schluss.trim() ? { schlusstext: schluss.trim() } : {}),
        positionen: alsEingabe(zeilen),
      };

      const gespeichert = bestand
        ? await api.aendern<Angebot>(`/api/angebote/${bestand.id}`, daten)
        : await api.senden<Angebot>('/api/angebote', daten);

      navigiere(`/angebote/${bestand?.id ?? gespeichert.id}`);
      return gespeichert;
    });
  }

  return (
    <Seite titel={bestand ? 'Angebot ändern' : 'Angebot schreiben'} untertitel="Entwurf">
      <form className="stapel" style={{ maxWidth: '1100px' }} onSubmit={(e) => void speichern(e)}>
        <Fehlermeldung text={vorgang.fehler} />

        {kontakte.daten && kunden.length === 0 ? (
          <Meldung ton="info" titel="Noch kein Kunde erfasst.">
            <Link to="/kontakte">Kunden anlegen</Link>.
          </Meldung>
        ) : null}

        <div className="karte">
          <div className="karte-kopf">
            <h2>Empfänger und Gültigkeit</h2>
          </div>
          <div className="karte-koerper formularraster">
            <KontaktWahl
              beschriftung="Empfänger"
              wert={kontaktId}
              setzeWert={setzeKontaktId}
              kontakte={kunden}
              typ="kunde"
              mitAnschrift
              pflicht
              neuGeladen={kontakte.neuLaden}
            />

            <Textfeld
              beschriftung="Angebotsdatum"
              art="date"
              wert={angebotsdatum}
              setzeWert={setzeAngebotsdatum}
              pflicht
            />

            <Textfeld
              beschriftung="Gültig für Tage"
              art="number"
              wert={gueltigkeit}
              setzeWert={setzeGueltigkeit}
              klasse="zahl"
              hinweis={
                bestand
                  ? `leer = unverändert (bis ${bestand.gueltigBis ?? 'offen'})`
                  : 'Danach gilt das Angebot als abgelaufen'
              }
            />

            <Textfeld
              beschriftung="Betreff"
              wert={betreff}
              setzeWert={setzeBetreff}
              breit
              maxLaenge={200}
              platzhalter="App-Entwicklung für den Museumsverein"
            />

            <Textfeld
              beschriftung="Leistung von"
              art="date"
              wert={leistungVon}
              setzeWert={setzeLeistungVon}
            />
            <Textfeld
              beschriftung="Leistung bis"
              art="date"
              wert={leistungBis}
              setzeWert={setzeLeistungBis}
            />
          </div>
        </div>

        <Positionen zeilen={zeilen} setzeZeilen={setzeZeilen} mitSteuer={!kleinunternehmer} />

        <div className="karte">
          <div className="karte-kopf">
            <h2>Texte</h2>
          </div>
          <div className="karte-koerper formularraster">
            <Textbereich
              beschriftung="Einleitung"
              wert={einleitung}
              setzeWert={setzeEinleitung}
              zeilen={2}
              breit
              platzhalter="Vielen Dank für Ihre Anfrage. Gerne biete ich Ihnen an:"
            />
            <Textbereich
              beschriftung="Schlusstext"
              wert={schluss}
              setzeWert={setzeSchluss}
              zeilen={2}
              breit
              platzhalter="Ich freue mich auf Ihre Rückmeldung."
            />
          </div>
        </div>

        <div className="reihe">
          <button type="submit" className="knopf haupt" disabled={!fertig || vorgang.laeuft}>
            {vorgang.laeuft ? 'Wird gespeichert …' : 'Entwurf speichern'}
          </button>
          <button
            type="button"
            className="knopf"
            onClick={() => navigiere(bestand ? `/angebote/${bestand.id}` : '/angebote')}
            disabled={vorgang.laeuft}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </Seite>
  );
}
